/**
 * @fileoverview Hook for managing Google Gemini OAuth connection
 * @description Checks if the user has connected their Google account for Gemini API access
 * and provides methods to connect/disconnect.
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { typedSupabase } from '@/lib/typed-supabase';
import { geminiAuthService } from '@/services/integrations/core/gemini-auth.service';
import { GoogleConnectionType } from '@/types/google-connection';
import { toast } from '@/components/ui/sonner';
import { logger } from '@/lib/logger';

interface UseGoogleGeminiConnectionReturn {
  /** Whether the user has an active Gemini OAuth connection */
  isConnected: boolean;
  /** Whether the connection status is loading */
  isLoading: boolean;
  /** The access token (when connected and valid) */
  accessToken: string | null;
  /** Initiate the Google OAuth flow for Gemini */
  connect: () => Promise<void>;
  /** Revoke the connection */
  disconnect: () => Promise<void>;
}

export function useGoogleGeminiConnection(): UseGoogleGeminiConnectionReturn {
  const { user } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [accessToken, setAccessToken] = useState<string | null>(null);

  // Check for existing connection on mount
  useEffect(() => {
    const checkConnection = async () => {
      if (!user?.id) {
        setIsLoading(false);
        return;
      }

      try {
        const { data, error } = await typedSupabase
          .from('user_google_connections')
          .select('access_token, expires_at, is_active')
          .eq('user_id', user.id)
          .eq('connection_type', GoogleConnectionType.GEMINI)
          .maybeSingle();

        if (error || !data || !data.is_active) {
          setIsConnected(false);
          setAccessToken(null);
        } else {
          setIsConnected(true);
          // Try to get a valid (refreshed) token
          const tokenResult = await geminiAuthService.getValidToken(user.id);
          if (tokenResult.success && tokenResult.data) {
            setAccessToken(tokenResult.data.access_token);
          } else if (tokenResult.error === 'TOKEN_REVOKED') {
            setIsConnected(false);
            setAccessToken(null);
            toast.error('Google connection revoked. Please reconnect.');
          } else {
            setAccessToken(null);
          }
        }
      } catch (err) {
        logger.error('[useGoogleGeminiConnection] Failed to check connection', { error: err });
        setIsConnected(false);
        setAccessToken(null);
      } finally {
        setIsLoading(false);
      }
    };

    checkConnection();
  }, [user?.id]);

  const connect = useCallback(async () => {
    if (!user?.id) {
      toast.error('Please sign in to connect your Google account');
      return;
    }

    try {
      const redirectUri = `${window.location.origin}/auth/google/callback`;
      const returnPath =
        `${window.location.pathname}${window.location.search}` || '/profile?tab=ai-settings';

      // Store state for CSRF protection and connection type
      sessionStorage.setItem('google_oauth_connection_type', GoogleConnectionType.GEMINI);
      sessionStorage.setItem('google_oauth_redirect', returnPath);

      const { url: authUrl, state } = await geminiAuthService.generateOAuthUrl(user.id, redirectUri);
      sessionStorage.setItem('google_oauth_state', state);
      sessionStorage.setItem(`google_oauth_connection_type:${state}`, GoogleConnectionType.GEMINI);
      sessionStorage.setItem(`google_oauth_redirect:${state}`, returnPath);
      window.location.href = authUrl;
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to start Google authorization');
    }
  }, [user?.id]);

  const disconnect = useCallback(async () => {
    if (!user?.id) {
      return;
    }

    try {
      const result = await geminiAuthService.revokeToken(user.id);
      if (result.success) {
        setIsConnected(false);
        setAccessToken(null);
        toast.success('Google Gemini connection removed');
      } else {
        toast.error(result.error || 'Failed to disconnect');
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to disconnect');
    }
  }, [user?.id]);

  return {
    isConnected,
    isLoading,
    accessToken,
    connect,
    disconnect,
  };
}
